import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { EditorContent, ReactNodeViewRenderer, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import { Placeholder } from "@tiptap/extension-placeholder";
import {
    LuBold,
    LuCamera,
    LuHeading3,
    LuImage,
    LuItalic,
    LuLink,
    LuList,
    LuListOrdered,
    LuQuote,
    LuTable,
    LuTrash2,
    LuUnderline,
} from "react-icons/lu";
import BlogImageView from "./BlogImageView.jsx";
import { hasBlogBody } from "./blogShared.jsx";
import {
    IMAGE_MAX,
    countBodyImages,
    extractPendingIds,
    extractS3Keys,
    headingsToH3,
    isBlogS3Key,
    rewritePendingToKey,
} from "./blogMedia.js";

const nextPendingId = () => (
    typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const BlogImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            s3Key: {
                default: null,
                parseHTML: (el) => el.getAttribute("data-s3-key")
                    || (isBlogS3Key(el.getAttribute("src")) ? el.getAttribute("src") : null),
                renderHTML: (attrs) => (attrs.s3Key ? { "data-s3-key": attrs.s3Key } : {}),
            },
            pendingId: {
                default: null,
                parseHTML: (el) => el.getAttribute("data-pending-id"),
                renderHTML: (attrs) => (attrs.pendingId && !attrs.s3Key
                    ? { "data-pending-id": attrs.pendingId }
                    : {}),
            },
        };
    },
    renderHTML({ HTMLAttributes }) {
        const key = HTMLAttributes["data-s3-key"]
            || (isBlogS3Key(HTMLAttributes.src) ? HTMLAttributes.src : null);
        if (key) {
            return ["img", { class: "blog__inline-image", "data-s3-key": key, src: key, alt: HTMLAttributes.alt || "" }];
        }
        const pendingId = HTMLAttributes["data-pending-id"];
        if (pendingId) {
            return ["img", {
                class: "blog__inline-image",
                "data-pending-id": pendingId,
                src: HTMLAttributes.src || "",
                alt: HTMLAttributes.alt || "",
            }];
        }
        return ["img", { class: "blog__inline-image", alt: HTMLAttributes.alt || "", src: HTMLAttributes.src || "" }];
    },
    addNodeView() {
        return ReactNodeViewRenderer(BlogImageView);
    },
});

const extensions = [
    StarterKit.configure({
        heading: { levels: [3] },
        code: false,
        codeBlock: false,
        strike: false,
        horizontalRule: false,
        link: {
            openOnClick: false,
            HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
        },
    }),
    BlogImage.configure({
        allowBase64: false,
        HTMLAttributes: { class: "blog__inline-image" },
    }),
    TableKit.configure({
        table: { resizable: true },
    }),
    Placeholder.configure({
        placeholder: "What should the team know?",
    }),
];

const sameHtml = (left, right) => String(left || "") === String(right || "");

const ToolBtn = ({ active, disabled, onClick, label, children }) => (
    <button
        type="button"
        className={`blog__editor-btn${active ? " is-on" : ""}`}
        onClick={onClick}
        disabled={disabled}
        aria-pressed={Boolean(active)}
        aria-label={label}
        title={label}
    >
        {children}
    </button>
);

const setLink = (editor) => {
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", previous);
    if (url === null) return;
    if (!url.trim()) {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
};

const imageFilesOf = (list) => [...(list || [])].filter((file) => file?.type?.startsWith("image/"));

const BlogBodyEditor = forwardRef(({ value, onChange, onError, onStartCapture, capturing, disabled }, ref) => {
    const fileRef = useRef(null);
    const errorRef = useRef(onError);
    const editorRef = useRef(null);
    const pendingRef = useRef({});
    const valueRef = useRef(value);
    const ignoreEmptyRef = useRef(true);
    errorRef.current = onError;
    valueRef.current = value;

    const prunePending = (html) => {
        const live = new Set(extractPendingIds(html));
        Object.entries(pendingRef.current).forEach(([id, row]) => {
            if (live.has(id)) return;
            if (row?.preview?.startsWith("blob:")) URL.revokeObjectURL(row.preview);
            delete pendingRef.current[id];
        });
    };

    const insertFiles = (files) => {
        const images = imageFilesOf(files);
        if (!images.length || !editorRef.current) return;
        const used = countBodyImages(editorRef.current.getHTML());
        if (used >= IMAGE_MAX) {
            errorRef.current?.(`You can add up to ${IMAGE_MAX} images.`);
            return;
        }
        const room = IMAGE_MAX - used;
        const take = images.slice(0, room);
        if (images.length > room) {
            errorRef.current?.(`You can add up to ${IMAGE_MAX} images.`);
        }
        take.forEach((file) => {
            const pendingId = nextPendingId();
            const preview = URL.createObjectURL(file);
            pendingRef.current[pendingId] = { file, preview };
            editorRef.current.chain().focus().insertContent({
                type: "image",
                attrs: { src: preview, pendingId, alt: "" },
            }).run();
        });
    };

    const publishImages = async (uploadImage) => {
        const editor = editorRef.current;
        if (!editor) return { html: "", imageKeys: [] };
        let html = editor.getHTML();
        for (const pendingId of extractPendingIds(html)) {
            const row = pendingRef.current[pendingId];
            if (!row?.file) continue;
            const { s3Key } = await uploadImage(row.file);
            if (!s3Key) continue;
            html = rewritePendingToKey(html, pendingId, s3Key);
            if (row.preview?.startsWith("blob:")) URL.revokeObjectURL(row.preview);
            delete pendingRef.current[pendingId];
        }
        editor.commands.setContent(html, { emitUpdate: false });
        onChange(html);
        return { html, imageKeys: extractS3Keys(html) };
    };

    const editor = useEditor({
        immediatelyRender: false,
        shouldRerenderOnTransaction: true,
        extensions,
        content: headingsToH3(value || ""),
        editorProps: {
            attributes: {
                class: "blog__editor-surface",
                "aria-label": "Post body",
            },
            handlePaste: (_view, event) => {
                const files = imageFilesOf(event.clipboardData?.files);
                if (!files.length) return false;
                event.preventDefault();
                insertFiles(files);
                return true;
            },
            handleDrop: (_view, event) => {
                const files = imageFilesOf(event.dataTransfer?.files);
                if (!files.length) return false;
                event.preventDefault();
                insertFiles(files);
                return true;
            },
        },
        onUpdate: ({ editor: next }) => {
            const html = next.getHTML();
            if (ignoreEmptyRef.current && !hasBlogBody(html) && hasBlogBody(valueRef.current)) return;
            prunePending(html);
            onChange(html);
        },
    });

    editorRef.current = editor;

    useEffect(() => {
        editor?.setEditable(!disabled);
    }, [editor, disabled]);

    useImperativeHandle(ref, () => ({
        insertFiles,
        publishImages,
        setHtml: (html) => {
            const next = headingsToH3(html || "");
            editorRef.current?.commands.setContent(next, { emitUpdate: false });
            onChange(next);
        },
    }), [onChange]);

    useEffect(() => () => {
        Object.values(pendingRef.current).forEach((row) => {
            if (row?.preview?.startsWith("blob:")) URL.revokeObjectURL(row.preview);
        });
        pendingRef.current = {};
    }, []);

    useEffect(() => {
        if (!editor) return;
        const next = headingsToH3(value || "");
        if (!sameHtml(next, editor.getHTML())) {
            editor.commands.setContent(next, { emitUpdate: false });
        }
        ignoreEmptyRef.current = false;
    }, [value, editor]);

    if (!editor) return <p className="blog__status">Loading editor…</p>;

    const inTable = editor.isActive("table");
    const atLimit = countBodyImages(editor.getHTML()) >= IMAGE_MAX;

    return (
        <div className="blog__editor">
            <div className="blog__editor-toolbar" role="toolbar" aria-label="Text formatting">
                <ToolBtn
                    label="Heading"
                    active={editor.isActive("heading", { level: 3 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                >
                    <LuHeading3 size="1.05rem" />
                </ToolBtn>
                <ToolBtn
                    label="Bold"
                    active={editor.isActive("bold")}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                >
                    <LuBold size="1rem" />
                </ToolBtn>
                <ToolBtn
                    label="Italic"
                    active={editor.isActive("italic")}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                >
                    <LuItalic size="1rem" />
                </ToolBtn>
                <ToolBtn
                    label="Underline"
                    active={editor.isActive("underline")}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                >
                    <LuUnderline size="1rem" />
                </ToolBtn>
                <span className="blog__editor-sep" aria-hidden="true" />
                <ToolBtn
                    label="Numbered list"
                    active={editor.isActive("orderedList")}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                >
                    <LuListOrdered size="1.05rem" />
                </ToolBtn>
                <ToolBtn
                    label="Bullet list"
                    active={editor.isActive("bulletList")}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                >
                    <LuList size="1.05rem" />
                </ToolBtn>
                <ToolBtn
                    label="Quote"
                    active={editor.isActive("blockquote")}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                >
                    <LuQuote size="1rem" />
                </ToolBtn>
                <ToolBtn
                    label="Link"
                    active={editor.isActive("link")}
                    onClick={() => setLink(editor)}
                >
                    <LuLink size="1rem" />
                </ToolBtn>
                <span className="blog__editor-sep" aria-hidden="true" />
                <ToolBtn
                    label="Insert image"
                    disabled={disabled || atLimit}
                    onClick={() => fileRef.current?.click()}
                >
                    <LuImage size="1.05rem" />
                </ToolBtn>
                <ToolBtn
                    label="Capture photo"
                    disabled={disabled || atLimit || capturing}
                    onClick={onStartCapture}
                >
                    <LuCamera size="1.05rem" />
                </ToolBtn>
                <ToolBtn
                    label="Insert table"
                    active={inTable}
                    onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                >
                    <LuTable size="1.05rem" />
                </ToolBtn>
                {inTable ? (
                    <>
                        <ToolBtn
                            label="Add column"
                            onClick={() => editor.chain().focus().addColumnAfter().run()}
                        >
                            Col+
                        </ToolBtn>
                        <ToolBtn
                            label="Add row"
                            onClick={() => editor.chain().focus().addRowAfter().run()}
                        >
                            Row+
                        </ToolBtn>
                        <ToolBtn
                            label="Delete table"
                            onClick={() => editor.chain().focus().deleteTable().run()}
                        >
                            <LuTrash2 size="1rem" />
                        </ToolBtn>
                    </>
                ) : null}
                <ToolBtn
                    label="Clear formatting"
                    onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
                >
                    Clear
                </ToolBtn>
            </div>
            <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                hidden
                onChange={(event) => {
                    insertFiles(event.target.files);
                    event.target.value = "";
                }}
            />
            <EditorContent editor={editor} />
        </div>
    );
});

export default BlogBodyEditor;
